import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import api from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { 
  HelpCircle, CreditCard, Receipt, Target,
  ChevronDown, Shield, ArrowLeft, ArrowRight, Zap, RefreshCw, Crown, Users, Lock, Sparkles, CheckCircle2
} from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { cn } from '../lib/utils';

const categoryConfig = {
  subscription: { icon: Crown, color: 'bg-[#FDF0EC]0/10 text-coral border-coral/20', label: 'Subscription' },
  commitment: { icon: Target, color: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20', label: 'Commitment' },
  features: { icon: Sparkles, color: 'bg-[#F4F3EE]0/10 text-violet-400 border-violet-500/20', label: 'Features' },
  goals: { icon: CheckCircle2, color: 'bg-blue-500/10 text-blue-400 border-blue-500/20', label: 'Goals' },
  security: { icon: Lock, color: 'bg-rose-500/10 text-rose-400 border-rose-500/20', label: 'Security' }
};

export default function FAQ() {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openItems, setOpenItems] = useState({});
  const [activeCategory, setActiveCategory] = useState('all');

  useEffect(() => {
    fetchFaqs();
  }, []);

  const fetchFaqs = async () => {
    try {
      const res = await api.get('/api/faq');
      setFaqs(res.data.faqs || []);
    } catch (error) {
      console.error('Failed to fetch FAQs:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleItem = (id) => {
    setOpenItems(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const filteredFaqs = activeCategory === 'all' 
    ? faqs 
    : faqs.filter(faq => faq.category === activeCategory);

  const categories = ['all', ...new Set(faqs.map(f => f.category))];

  return (
    <div className={cn(
      "min-h-screen",
      isDark ? "bg-[#050505] text-white" : "bg-[#FAF9F5] text-[#1A1A1A]"
    )}>
      {/* Navigation */}
      <nav className={cn(
        "fixed top-0 left-0 right-0 z-50 border-b backdrop-blur-xl",
        isDark ? "border-white/5 bg-[#050505]/80" : "border-[#EEEAE0] bg-white/80"
      )}>
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <button 
            onClick={() => navigate('/')}
            className={cn(
              "flex items-center gap-2 transition-colors",
              isDark ? "text-zinc-400 hover:text-white" : "text-[#5F5F5F] hover:text-[#1A1A1A]"
            )}
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm font-medium">Back</span>
          </button>
          <div className="flex items-center gap-2 group">
            <div className="w-8 h-8 rounded-lg overflow-hidden monk-hover">
              <img src="https://customer-assets.emergentagent.com/job_fc70e8a7-91d2-4d7f-977d-e07f378252ba/artifacts/zlmzll26_Monk%20Mode.png" alt="Monk Mode" className="w-full h-full object-cover" />
            </div>
            <span className={cn("text-xl font-bold tracking-tight group-hover:text-coral transition-colors", isDark ? "text-white" : "text-[#1A1A1A]")}>Monk Mode</span>
          </div>
          <Button 
            onClick={() => navigate('/auth')}
            className="rounded-full px-6 btn-glossy font-medium"
          >
            Get Started
          </Button>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-6 pt-28 pb-20">
        {/* Clean header */}
        <div className="mb-12">
          <p className="sl-section-tag mb-3">FREQUENTLY ASKED</p>
          <h1 className="text-4xl md:text-5xl font-semibold tracking-[-0.03em] text-[#1A1A1A] leading-[1.05]">
            Questions, answered.
          </h1>
          <p className="mt-4 text-base sm:text-lg text-[#5F5F5F] max-w-xl">
            Everything about subscriptions, shared goals, and keeping your word.
          </p>
        </div>

        {/* Trust Badges */}
        <div className="flex flex-wrap items-center justify-center gap-4 mb-12">
          <div className={cn(
            "flex items-center gap-2 px-4 py-2 rounded-full text-sm border",
            isDark ? "glass border-white/10" : "bg-white border-[#E5E2DA] shadow-sm"
          )}>
            <Shield className="w-4 h-4 text-coral" />
            <span className={isDark ? "text-zinc-300" : "text-[#5F5F5F]"}>Secure via Stripe</span>
          </div>
          <div className={cn(
            "flex items-center gap-2 px-4 py-2 rounded-full text-sm border",
            isDark ? "glass border-white/10" : "bg-white border-[#E5E2DA] shadow-sm"
          )}>
            <Zap className="w-4 h-4 text-coral" />
            <span className={isDark ? "text-zinc-300" : "text-[#5F5F5F]"}>$4.99/month</span>
          </div>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-2 mb-8">
          {categories.map(category => {
            const config = categoryConfig[category];
            return (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={cn(
                  "px-4 py-2 rounded-full text-sm font-medium transition-all",
                  activeCategory === category 
                    ? 'btn-glossy' 
                    : isDark 
                      ? 'glass text-zinc-400 hover:text-white border border-white/10 hover:border-[#F5D5CB]'
                      : 'bg-white text-[#5F5F5F] hover:text-[#1A1A1A] border border-[#E5E2DA] hover:border-amber-400 shadow-sm'
                )}
              >
                {category === 'all' ? 'All Questions' : config?.label || category}
              </button>
            );
          })}
        </div>

        {/* FAQ List */}
        <div className="space-y-3">
          {loading ? (
            <div className={cn("text-center py-12", isDark ? "text-zinc-500" : "text-[#9E8778]")}>Loading...</div>
          ) : filteredFaqs.length === 0 ? (
            <div className={cn("text-center py-12", isDark ? "text-zinc-500" : "text-[#9E8778]")}>No FAQs found</div>
          ) : (
            filteredFaqs.map((faq) => {
              const config = categoryConfig[faq.category] || { 
                icon: HelpCircle, 
                color: isDark ? 'bg-zinc-800 text-zinc-400 border-zinc-700' : 'bg-slate-100 text-[#5F5F5F] border-slate-300'
              };
              const IconComponent = config.icon;
              
              return (
                <Collapsible 
                  key={faq.id} 
                  open={openItems[faq.id]}
                  onOpenChange={() => toggleItem(faq.id)}
                >
                  <CollapsibleTrigger className="w-full">
                    <div className={cn(
                      "flex items-center justify-between p-5 rounded-xl transition-all group",
                      isDark ? "glass-card" : "bg-white border border-[#EEEAE0] shadow-sm hover:shadow-md",
                      openItems[faq.id] && (isDark ? 'border-[#F5D5CB]' : 'border-amber-400')
                    )}>
                      <div className="flex items-center gap-4">
                        <div className={`p-2 rounded-lg border ${config.color}`}>
                          <IconComponent className="w-4 h-4" />
                        </div>
                        <span className={cn(
                          "font-medium text-left transition-colors",
                          isDark ? "text-zinc-200 group-hover:text-white" : "text-[#1A1A1A] group-hover:text-slate-900"
                        )}>
                          {faq.question}
                        </span>
                      </div>
                      <ChevronDown className={cn(
                        "w-5 h-5 transition-transform",
                        isDark ? "text-zinc-500" : "text-[#9E8778]",
                        openItems[faq.id] && 'rotate-180'
                      )} />
                    </div>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className={cn(
                      "px-5 py-4 mx-5 mt-1 mb-2 rounded-lg border-l-2 border-coral/50",
                      isDark ? "bg-zinc-900/20" : "bg-[#FDF0EC]/50"
                    )}>
                      <p className={isDark ? "text-zinc-400 leading-relaxed" : "text-[#5F5F5F] leading-relaxed"}>{faq.answer}</p>
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              );
            })
          )}
        </div>

        {/* How Subscription Works */}
        <Card className={cn("mt-12 overflow-hidden", isDark ? "glass-card" : "bg-white border-[#EEEAE0] shadow-sm")}>
          <CardContent className="p-8">
            <h2 className={cn("text-2xl font-bold mb-6 flex items-center gap-2", isDark ? "text-gradient" : "text-[#1A1A1A]")}>
              <CreditCard className="w-6 h-6 text-coral" />
              How Monk Mode Pro Works
            </h2>
            <div className="space-y-6">
              {[
                { num: '1', title: 'Subscribe to Monk Mode Pro', desc: '$4.99/month unlocks unlimited goals, shared goals, and the weekly tracker' },
                { num: '2', title: 'Set your commitments', desc: 'Create goals with clear targets and deadlines. Organize them into groups.' },
                { num: '3', title: 'Show up every day', desc: 'Track progress on the calendar, build the streak, keep the promise' },
                { num: '4', title: 'Share the load', desc: 'Invite friends to shared goals. Accountability is quieter when it isn\u2019t alone.' }
              ].map((step, i) => (
                <div key={i} className="flex items-start gap-4">
                  <div className={cn(
                    "w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 border",
                    isDark ? "bg-[#FDF0EC]0/20 border-[#F5D5CB] glow-amber-sm" : "bg-[#FDF0EC] border-[#F5E9DE]"
                  )}>
                    <span className={isDark ? "font-bold text-coral" : "font-bold text-coral"}>{step.num}</span>
                  </div>
                  <div>
                    <p className={cn("font-medium", isDark ? "text-zinc-200" : "text-[#1A1A1A]")}>{step.title}</p>
                    <p className={cn("text-sm", isDark ? "text-zinc-500" : "text-[#5F5F5F]")}>{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* CTA */}
        <div className="mt-12 text-center">
          <p className={cn("mb-6", isDark ? "text-zinc-400" : "text-[#5F5F5F]")}>Ready to start achieving your goals?</p>
          <Button 
            onClick={() => navigate('/auth')}
            className="btn-glossy rounded-full px-8 py-6 text-base font-medium glow-amber"
          >
            Get Started Free
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>

      {/* Footer */}
      <footer className={cn("border-t py-8 px-6", isDark ? "border-white/5" : "border-[#EEEAE0]")}>
        <div className={cn("max-w-7xl mx-auto flex items-center justify-center gap-6 text-sm", isDark ? "text-zinc-500" : "text-[#9E8778]")}>
          <span>© 2025 Monk Mode</span>
        </div>
      </footer>
    </div>
  );
}
